import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {setIssueFieldValueMutation} from './__generated__/setIssueFieldValueMutation.graphql'

export function commitSetIssueFieldValueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {
    issueId: string
    issueFieldId: string
    newValue: string
  }
  onCompleted?: () => void
  onError?: (error?: Error) => void
}) {
  return commitMutation<setIssueFieldValueMutation>(environment, {
    mutation: graphql`
      mutation setIssueFieldValueMutation($input: SetIssueFieldValueInput!) {
        setIssueFieldValue(input: $input) {
          issueFieldValue {
            ... on IssueFieldTextValue {
              id
              value
            }
            ... on IssueFieldSingleSelectValue {
              id
              name
              color
              description
            }
          }
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input,
    },
    onCompleted: () => {
      onCompleted?.()
    },
    onError: error => onError?.(error),
    updater: store => {
      // getting all the data from the store
      const issue = store.get(input.issueId)
      const fieldValues = issue?.getLinkedRecord('issueFieldValues', {first: 25})
      const nodes = fieldValues?.getLinkedRecords('nodes') || []
      const payload = store.getRootField('setIssueFieldValue')
      const newValue = payload.getLinkedRecord('issueFieldValue')

      // If the server returns null (deselection), set value to null on the existing node
      const prev_value = nodes.find(node => {
        const field = node.getLinkedRecord('field')
        return field && field.getDataID() === input.issueFieldId
      })

      if (!newValue && prev_value) {
        if (prev_value.getType() === 'IssueFieldTextValue') {
          prev_value.setValue(null, 'value')
        } else if (prev_value.getType() === 'IssueFieldSingleSelectValue') {
          for (const key of ['name', 'color', 'description']) {
            prev_value.setValue(null, key)
          }
        }
        // replace the previous node with the cleared one, preserving order
        const updatedNodes = nodes.map(node => {
          const field = node.getLinkedRecord('field')
          return field && field.getDataID() === input.issueFieldId ? prev_value : node
        })
        fieldValues?.setLinkedRecords(updatedNodes, 'nodes')
        return
      }

      // we need to ensure the new value node has the correct field linked (for the cases when the user accidentally deselects an option and then selects an option again)
      if (newValue && !newValue.getLinkedRecord('field')) {
        const fieldRecord = store.get(input.issueFieldId)
        if (fieldRecord) newValue.setLinkedRecord(fieldRecord, 'field')
      }

      // if the server returns a new value, replace the old one in-place to preserve order
      // or append if the field was not present before
      let found = false
      const updatedNodes = nodes.map(node => {
        const field = node.getLinkedRecord('field')
        if (field && field.getDataID() === input.issueFieldId) {
          found = true
          return newValue
        }
        return node
      })
      const finalNodes = found ? updatedNodes : [...updatedNodes, newValue]
      fieldValues?.setLinkedRecords(finalNodes, 'nodes')
    },
  })
}
